/* museum-season.js — a Dead Warrior Society season, opened from its place's pin
 * on the chart or its card in the Field Atlas (V2.72 K22; the pin was a seal
 * until K24).
 *
 * R3 "The War Room" from the DWS canvas (Josh, 2026-09-21: "R3 as the
 * winner"). The season's cover runs across the top with its numeral, its
 * title and the way to play it, and every episode sits under it as a picture
 * to pick. On a phone the cover stays and the episodes become R2's list,
 * because ten titles read better stacked than in a two-up grid a finger wide.
 *
 *   FORUM_SEASON.mount({ host, icon, onPlay })
 *   FORUM_SEASON.open(season, opener)   a one-episode season plays at once
 *   FORUM_SEASON.close()
 *   FORUM_SEASON.isOpen()
 *   FORUM_SEASON.mark(index)            the episode the player is on
 *
 * The episodes play in the atlas's player, which stands in front of this
 * (z 62 over 60), so closing the player lands back here with the episode
 * still under focus. A season is { no, title, sub, place, runtime,
 * playlistUrl, cover, episodes: [[id, title, length, flag]] } — museum-data.js
 * builds them.
 */
(function () {
  'use strict';

  if (window.FORUM_SEASON) return;   /* shopify:section:load can run this twice */

  var host = null, dialog = null, scroller = null;
  var els = {};
  var icon = '';
  var onPlay = null;
  var current = null;
  var returnFocus = null;
  var mounted = false;

  var CLOSE_SVG = '<svg width="15" height="15" viewBox="0 0 26 26" aria-hidden="true">' +
    '<path d="M6 6 L20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' +
    '<path d="M20 6 L6 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
  var PLAY_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z"/></svg>';
  var OUT_SVG = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M7 17L17 7M9 7h8v8"/></svg>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function thumb(id, size) { return 'https://i.ytimg.com/vi/' + id + '/' + size + '.jpg'; }
  function episodesLabel(n) { return n + (n === 1 ? ' episode' : ' episodes'); }
  function quiet() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function build() {
    host.classList.add('sv-host');
    host.innerHTML =
      '<div class="sv" role="dialog" aria-modal="true" aria-labelledby="svTitle" tabindex="-1">' +
        '<button type="button" class="sv-close" aria-label="Close the season">' + CLOSE_SVG + '</button>' +
        '<div class="sv-scroll">' +
          '<header class="sv-hero">' +
            '<img class="sv-cover" alt="" decoding="async">' +
            '<span class="sv-shade" aria-hidden="true"></span>' +
            '<span class="sv-numeral" aria-hidden="true"></span>' +
            '<div class="sv-copy">' +
              '<p class="sv-eyebrow"><span class="sv-icon" aria-hidden="true"></span><span class="sv-eyebrow-text"></span></p>' +
              '<h2 class="sv-title" id="svTitle"></h2>' +
              '<p class="sv-sub"></p>' +
              '<p class="sv-meta"></p>' +
              '<div class="sv-actions">' +
                '<button type="button" class="sv-play">' + PLAY_SVG + '<span>Play episode 01</span></button>' +
                '<a class="sv-yt" href="#" target="_blank" rel="noopener noreferrer">' +
                  '<span>Full season on YouTube</span>' + OUT_SVG + '</a>' +
              '</div>' +
            '</div>' +
          '</header>' +
          '<section class="sv-episodes" aria-labelledby="svCount">' +
            '<div class="sv-head">' +
              '<h3 class="sv-count" id="svCount"></h3>' +
              '<span class="sv-rule" aria-hidden="true"></span>' +
              '<span class="sv-runtime"></span>' +
            '</div>' +
            '<ol class="sv-grid"></ol>' +
          '</section>' +
        '</div>' +
      '</div>';

    dialog = host.querySelector('.sv');
    scroller = host.querySelector('.sv-scroll');
    els = {
      close: host.querySelector('.sv-close'),
      cover: host.querySelector('.sv-cover'),
      numeral: host.querySelector('.sv-numeral'),
      icon: host.querySelector('.sv-icon'),
      eyebrow: host.querySelector('.sv-eyebrow-text'),
      title: host.querySelector('.sv-title'),
      sub: host.querySelector('.sv-sub'),
      meta: host.querySelector('.sv-meta'),
      play: host.querySelector('.sv-play'),
      playLabel: host.querySelector('.sv-play span'),
      yt: host.querySelector('.sv-yt'),
      count: host.querySelector('.sv-count'),
      runtime: host.querySelector('.sv-runtime'),
      grid: host.querySelector('.sv-grid')
    };

    els.close.addEventListener('click', close);
    els.play.addEventListener('click', function () { play(0); });
    els.grid.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('.sv-ep') : null;
      if (b) play(Number(b.getAttribute('data-i')));
    });
    /* A cover that YouTube never made at full size falls back once. */
    els.cover.addEventListener('error', function () {
      var alt = els.cover.getAttribute('data-fallback');
      if (alt && els.cover.getAttribute('src') !== alt) els.cover.setAttribute('src', alt);
    });
    /* The player says which episode it moved to (its own previous and next),
       so coming back here lands on the one that was playing. */
    window.addEventListener('museum:episode', function (e) {
      var d = e.detail || {};
      if (current && d.season === current) mark(d.index);
    });
  }

  function fill(s) {
    var n = s.episodes.length;
    els.cover.setAttribute('data-fallback', thumb(s.cover, 'hqdefault'));
    els.cover.setAttribute('src', thumb(s.cover, 'maxresdefault'));
    els.numeral.textContent = s.no;
    els.icon.innerHTML = icon;
    els.eyebrow.textContent = 'Dead Warrior Society · Season ' + s.no;
    els.title.textContent = s.title;
    els.sub.textContent = s.sub || '';
    els.meta.textContent = [s.place, episodesLabel(n), s.runtime].filter(Boolean).join(' · ');
    els.playLabel.textContent = 'Play episode 01';
    els.yt.setAttribute('href', s.playlistUrl);
    els.count.textContent = episodesLabel(n);
    els.runtime.textContent = s.runtime || '';
    els.grid.innerHTML = s.episodes.map(function (ep, i) {
      var flag = ep[3] ? '<span class="sv-flag">' + esc(ep[3]) + '</span>' : '';
      return '<li><button type="button" class="sv-ep" data-i="' + i + '">' +
        '<span class="sv-thumb"><img src="' + thumb(ep[0], 'mqdefault') + '" alt="" width="320" height="180" loading="lazy" decoding="async">' +
          '<span class="sv-len">' + esc(ep[2]) + '</span></span>' +
        '<span class="sv-ep-text">' +
          '<span class="sv-ep-no">Episode ' + pad2(i + 1) + '<span class="sv-ep-len">' + esc(ep[2]) + '</span>' + flag + '</span>' +
          '<span class="sv-ep-title">' + esc(ep[1]) + '</span>' +
        '</span></button></li>';
    }).join('');
  }

  function play(i) {
    if (!current || !current.episodes[i]) return;
    mark(i);
    if (onPlay) onPlay(current, i);
  }

  function mark(i) {
    if (!els.grid) return;
    els.grid.querySelectorAll('.sv-ep').forEach(function (b, n) {
      var on = n === i;
      b.classList.toggle('is-current', on);
      if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    });
  }

  function focusables() {
    return Array.prototype.filter.call(
      dialog.querySelectorAll('button, a[href]'),
      function (n) { return !n.disabled && n.offsetParent !== null; });
  }

  /* Capture phase, like the atlas's Escape, and for the same reason: the
     player is in front of this and has its own Escape on `window`, so this
     stands down while it is open and leaves that key to it. */
  function onKey(e) {
    if (!current) return;
    if (document.body.classList.contains('film-open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'Tab') return;
    var list = focusables();
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    var at = document.activeElement;
    if (e.shiftKey && (at === first || !dialog.contains(at))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (at === last || !dialog.contains(at))) { e.preventDefault(); first.focus(); }
  }

  function open(season, opener) {
    if (!mounted || !season || !season.episodes || !season.episodes.length) return;
    /* One video is not a season to choose from: it just plays (Zach). */
    if (season.episodes.length === 1) {
      if (onPlay) onPlay(season, 0);
      return;
    }
    var already = !!current;
    current = season;
    if (!already) returnFocus = opener || document.activeElement;
    fill(season);
    mark(-1);
    if (scroller) scroller.scrollTop = 0;
    host.classList.add('sv-open');
    document.body.classList.add('season-open');
    /* Flushed before the class that fades it in, so the fade has somewhere to
       start from without waiting for an animation frame a hidden tab never
       delivers. */
    void host.offsetWidth;
    host.classList.add('sv-in');
    if (!already) window.addEventListener('keydown', onKey, true);
    setTimeout(function () {
      if (current === season) els.play.focus({ preventScroll: true });
    }, quiet() ? 0 : 60);
  }

  function close() {
    if (!current) return;
    current = null;
    host.classList.remove('sv-in', 'sv-open');
    document.body.classList.remove('season-open');
    window.removeEventListener('keydown', onKey, true);
    var back = returnFocus;
    returnFocus = null;
    setTimeout(function () {
      if (back && back.focus && document.documentElement.contains(back) && back !== document.body) {
        back.focus({ preventScroll: true });
      }
    }, 0);
  }

  window.FORUM_SEASON = {
    mount: function (opts) {
      opts = opts || {};
      if (opts.icon) icon = opts.icon;
      if (typeof opts.onPlay === 'function') onPlay = opts.onPlay;
      if (mounted || !opts.host) return;
      host = opts.host;
      build();
      mounted = true;
    },
    open: open,
    close: close,
    isOpen: function () { return !!current; },
    mark: mark,
    version: '1'
  };
})();
